'use client'

import { useEffect, useMemo, useState } from 'react'
import { isValidEmail, isValidPhoneNumber } from '@/utils/util'
import { useAuth } from './useAuth'

type ValidationResult = { ok: true } | { ok: false; error: string }

const generateRandomFullName = () => `Хэрэглэгч#${Math.floor(1000 + Math.random() * 9000)}`

export function useCustomerDetails() {
  const { session } = useAuth()
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [additionalPhone, setAdditionalPhone] = useState('')

  useEffect(() => {
    if (session?.user?.id) {
      if (session.user?.firstName || session.user?.lastName) {
        setFullName(`${session.user?.lastName || ''} ${session.user?.firstName || ''}`.trim())
      }
      setEmail(session.user?.email || '')
      setPhone(session.user?.phone || '')
    }
  }, [session])

  // Warn on tab close / refresh once the customer has typed something.
  // ponytail: no cleanup on submit needed — useCheckoutSubmit navigates with
  // router.push, and beforeunload does not fire on client-side navigation.
  const [dirty, setDirty] = useState(false)
  useEffect(() => {
    if (!dirty) return
    const warn = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  const touched = (set: (value: string) => void) => (value: string) => {
    setDirty(true)
    set(value)
  }

  const validate = ({ hasDigitalProduct }: { hasDigitalProduct: boolean }): ValidationResult => {
    if (!phone) return { ok: false, error: 'Утасны дугаараа оруулна уу' }
    if (hasDigitalProduct && !email) return { ok: false, error: 'Дижитал бараа авахын тулд имэйл хаяг оруулна уу' }
    if (email && !isValidEmail(email)) return { ok: false, error: 'Имэйл хаяг буруу байна' }
    if (!isValidPhoneNumber(phone)) return { ok: false, error: 'Утасны дугаар буруу байна' }
    if (additionalPhone && !isValidPhoneNumber(additionalPhone)) return { ok: false, error: 'Нэмэлт утасны дугаар буруу байна' }
    return { ok: true }
  }

  const getCustomerName = () => fullName.trim() || generateRandomFullName()

  return useMemo(() => ({
    fullName, setFullName: touched(setFullName),
    email, setEmail: touched(setEmail),
    phone, setPhone: touched(setPhone),
    additionalPhone, setAdditionalPhone: touched(setAdditionalPhone),
    validate,
    getCustomerName,
  }), [fullName, email, phone, additionalPhone])
}
